import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { MessageEnvelope } from '@cds/push';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BetslipUpsellConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import { Store } from '@ngrx/store';
import { clone } from 'lodash-es';
import { combineLatest, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import PickPushUpdaterService from '../../../integration/services/push-updaters/pick-push-updater.service';
import { UpsellEvent } from '../models';
import { UpsellEventFactoryService } from '../services/upsell-event-factory.service';
import { UpsellPushService } from '../services/upsell-push.service';
import { UpsellSettingsService } from '../services/upsell-settings.service';
import { UpsellVisibilityService } from '../services/upsell-visibility.service';
import UpsellTrackingActions from '../state/upsell-tracking.actions';
import { UpsellTracking } from '../state/upsell-tracking.effects';

@Component({
    selector: 'bs-upsell-container',
    templateUrl: './upsell-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class UpsellContainerComponent implements OnInit, OnDestroy {
    @HostBinding('class') className = 'betslip-upsell-container';
    @HostBinding('class.expanded') isExpanded = false;
    @HostBinding('class.single') isSingleUpsell = false;
    @HostBinding('class.upsell-data') upsellData = false;
    @HostBinding('style.background-image') bgImage?: SafeStyle;

    isUpsellVisible: boolean;

    headerIcon: string;
    seeLessText: string;
    headerTitle: string;

    private eventsSubscription?: EventSubscription<EventModel>;

    upsellEvents: UpsellEvent[] = [];
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private sitecore: Sitecore,
        public betslipUpsellConfig: BetslipUpsellConfig,
        private gridSubscriptionService: GridSubscriptionService,
        private upsellVisibilityService: UpsellVisibilityService,
        private upsellEventFactory: UpsellEventFactoryService,
        private betslipUpsellStoreService: UpsellSettingsService,
        private changeDetectorRef: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        private store: Store<IBetslipRootState>,
        private upsellPushService: UpsellPushService,
        private pickPushUpdater: PickPushUpdaterService,
    ) {}

    ngOnInit(): void {
        this.headerTitle = this.sitecore.betslipUpsell.messages.HeaderTitle;
        this.headerIcon = this.sitecore.betslipUpsell.messages.HeaderIcon;
        this.seeLessText = this.sitecore.betslipUpsell.messages.SeeLessCTAText;
        if (this.betslipUpsellConfig.overrideDontShow) {
            this.betslipUpsellStoreService.setUpsellVisibility(true);
        }
        this.isExpanded = this.betslipUpsellConfig.isBetslipUpsellExpanded;

        combineLatest([this.upsellEventFactory.getUpsellEvents$(), this.upsellVisibilityService.isUpsellVisible$()])
            //TODO make sure this getUpsellEvents isn't emitting 10+ times
            //Update @Monzer ignored during v3 cleanup (but still valid)
            .pipe(
                map(([upsellEvents, isVisible]) => {
                    this.isSingleUpsell = upsellEvents.length === 1;
                    const eventSubscriptionRequests = upsellEvents.map((upsellEvent) => ({
                        event: clone(upsellEvent.event),
                        callback: (updatedEvent: EventModel, message: MessageEnvelope) => {
                            const bestSellingOption = upsellEvent.bestSellingOption;
                            const updatedOptionGroup = updatedEvent.optionGroups.find(
                                (group) => group.id === bestSellingOption.optionGroupId.toString(),
                            );

                            if (!updatedOptionGroup) {
                                return;
                            }

                            upsellEvent.event = updatedEvent;
                            upsellEvent.bestSellingOption = updatedOptionGroup.options.find((option) => option.id === bestSellingOption.id)!;
                            const [updatedPick, _] = this.pickPushUpdater.updatePick(upsellEvent.bestSellingPick, message);
                            upsellEvent.bestSellingPick = updatedPick;

                            this.upsellPushService.updateEvent(upsellEvent);
                            this.changeDetectorRef.markForCheck();
                        },
                    }));

                    this.eventsSubscription?.unsubscribe();
                    this.eventsSubscription = this.gridSubscriptionService.subscribe(eventSubscriptionRequests);

                    this.upsellEvents = upsellEvents;
                    this.isUpsellVisible = isVisible;
                    this.changeDetectorRef.markForCheck();

                    this.upsellData = isVisible && this.upsellEvents.length > 0;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.bgImage = this.getSanitizedImageUrl(this.betslipUpsellConfig.moduleBackgroundImage);
    }

    ngOnDestroy(): void {
        this.eventsSubscription?.unsubscribe();
    }

    toggle(): void {
        this.isExpanded = !this.isExpanded;

        this.store.dispatch(
            UpsellTrackingActions.track({
                eventDetails: this.isExpanded ? UpsellTracking.Expand : UpsellTracking.Collapse,
                action: UpsellTracking.Click,
            }),
        );
    }

    seeLessClicked(): void {
        this.betslipUpsellStoreService.setUpsellVisibility(false);

        this.store.dispatch(
            UpsellTrackingActions.track({
                eventDetails: UpsellTracking.ShowLess,
                action: UpsellTracking.Click,
            }),
        );
    }

    trackByFn(index: number, upsellEvent: UpsellEvent): string | number {
        return upsellEvent.event.id || index;
    }

    private getSanitizedImageUrl(url?: string): SafeStyle | undefined {
        if (!url) {
            return undefined;
        }

        return this.sanitizer.bypassSecurityTrustStyle(`url(${url}`);
    }
}
